import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { cn } from '../../utils/cn'
import BrandMark from './BrandMark'

// Sticky top navbar for the logged-out landing page. Replaces the previous
// bottom floating pill (FloatingNavigation) as the primary way to move
// around the page — one wayfinding surface instead of two competing ones.
// Links point at section ids that already exist on the page; nothing here
// invents a destination.
const NAV_LINKS = [
  { id: 'objectives', label: 'Objectives' },
  { id: 'framework', label: 'Framework' },
  { id: 'health-data', label: 'Diseases' },
  { id: 'team', label: 'Team' },
  { id: 'partners', label: 'Partners' },
]

// Ring-only focus treatment (no offset): the bar sits over two very
// different backgrounds (transparent-over-light-hero at the top of the page,
// white/blurred once scrolled) and an offset ring's default offset color
// would clash with one of the two. A plain ring reads cleanly on both.
const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-0'

const LandingNavbar = () => {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  // Same offset-scroll approach as FloatingNavigation's scrollToSection —
  // native `#anchor` jumps would land the section flush under this fixed bar.
  const scrollToSection = useCallback((id) => {
    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
      return
    }
    const element = document.getElementById(id)
    if (!element) return
    const headerOffset = 88
    const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset
    window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
  }, [prefersReducedMotion])

  const handleLinkClick = (id) => (e) => {
    e.preventDefault()
    setMobileOpen(false)
    scrollToSection(id)
  }

  // The bar also goes opaque while the mobile panel is open (even before any
  // scrolling) so a white dropdown never hangs off a transparent bar.
  // The hero itself is light (gray-50/blue-50/white gradient), so the
  // at-top state uses the same dark-on-light text as the scrolled state —
  // only the chrome (background/border/shadow) differs between the two.
  const opaque = scrolled || mobileOpen
  const chromeClasses = opaque
    ? 'bg-white/90 backdrop-blur-xl border-b border-gray-200/70 shadow-elev-1'
    : 'bg-transparent border-b border-transparent'
  const textClasses = 'text-gray-700 hover:text-primary-700'

  return (
    <header className={cn('fixed inset-x-0 top-0 z-40 transition-colors duration-300', chromeClasses)}>
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <a
          href="#hero"
          onClick={handleLinkClick('hero')}
          className={cn('flex items-center gap-2 rounded-2xl sm:gap-2.5', FOCUS_RING)}
          aria-label="IEWRS — home"
        >
          {/* Compact brand lockup: the authentic emblem + a crisp two-tone
              IEWRS wordmark. The full stacked logo (with tagline) lives at
              large size in the footer, where it stays legible. */}
          <BrandMark iconClass="h-8 sm:h-9" wordmarkClass="text-xl sm:text-2xl" />
        </a>

        {/* Desktop section links */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Page sections">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={handleLinkClick(link.id)}
              className={cn(
                'rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200',
                textClasses,
                FOCUS_RING
              )}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden items-center gap-2 lg:flex">
          <Link
            to="/login"
            className={cn(
              'rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200',
              textClasses,
              FOCUS_RING
            )}
          >
            Sign In
          </Link>
          <Link to="/request-access" className={cn('btn-brand btn-sm', FOCUS_RING)}>
            Request Access
          </Link>
        </div>

        {/* Mobile trigger */}
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-controls="landing-mobile-menu"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-200 lg:hidden',
            textClasses,
            FOCUS_RING
          )}
        >
          {mobileOpen ? <X className="h-5 w-5" strokeWidth={1.75} /> : <Menu className="h-5 w-5" strokeWidth={1.75} />}
        </button>
      </div>

      {/* Mobile panel */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="landing-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="overflow-hidden border-t border-gray-200/70 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-1 px-4 py-4" aria-label="Page sections">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={handleLinkClick(link.id)}
                  className="rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                >
                  {link.label}
                </a>
              ))}
              <div className="mt-2 flex flex-col gap-2 border-t border-gray-100 pt-3">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="btn-secondary btn-md w-full"
                >
                  Sign In
                </Link>
                <Link
                  to="/request-access"
                  onClick={() => setMobileOpen(false)}
                  className="btn-brand btn-md w-full"
                >
                  Request Access
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default LandingNavbar
